import { create } from 'zustand';
import { getOrganizations, getOrganization, addOrganization as addOrganizationService, updateOrganization, deleteOrganization as deleteOrganizationService } from '../services/organization.service';
import { Organization, AddOrganization, UpdateOrganizationRequest } from '../types/organization.type';
import { extractOrganizationFromResponse, mergeOrganizationWithPayload, hasOrganizationId } from '../utils/organization.utils';
import { normalizeOrganizationServiceStatus } from '../utils/organizationServiceStatus.utils';

const normalizeOrganizations = (list: Organization[]) =>
  list.map((org) => normalizeOrganizationServiceStatus(org));

interface OrganizationState {
  organizations: Organization[];
  organization: Organization | null;
  isLoading: boolean;
  error: string | null;
  fetchOrganizations: (params?: { limit?: number; offset?: number; name?: string }) => Promise<void>;
  fetchAllOrganizations: () => Promise<void>;
  fetchOrganization: (organizationId: string) => Promise<void>;
  addOrganization: (data: AddOrganization) => Promise<Organization>;
  updateOrganization: (id: string, data: UpdateOrganizationRequest) => Promise<void>;
  deleteOrganization: (id: string) => Promise<void>;
}

export const useOrganizationStore = create<OrganizationState>((set, get) => ({
  organizations: [],
  organization: null,
  isLoading: false,
  error: null,

  fetchOrganizations: async (params?: { limit?: number; offset?: number; name?: string }) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getOrganizations(params);
      const list = ((response.result_list ?? []) as Array<Organization | null | undefined>).filter(
        (org): org is Organization => Boolean(org?.id && org?.name),
      );
      set({ organizations: normalizeOrganizations(list), isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Error fetching organizations', isLoading: false });
    }
  },

  fetchAllOrganizations: async () => {
    const hasCached = get().organizations.length > 0;
    if (!hasCached) set({ isLoading: true, error: null });
    try {
      const response = await getOrganizations({ limit: 500, offset: 0 });
      const list = ((response.result_list ?? []) as Array<Organization | null | undefined>).filter(
        (org): org is Organization => Boolean(org?.id && org?.name),
      );
      set({ organizations: normalizeOrganizations(list), isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Error fetching all organizations', isLoading: false });
    }
  },

  fetchOrganization: async (organizationId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getOrganization(organizationId);
      const fetched = extractOrganizationFromResponse(response) ?? (response as Organization);
      set((state) => {
        const prev = state.organization?.id === organizationId ? state.organization : null;
        const next = hasOrganizationId(fetched) ? fetched : null;
        return {
          organization: next
            ? {
                ...(prev ?? {}),
                ...next,
                legal_entity_type: next.legal_entity_type ?? prev?.legal_entity_type ?? null,
                has_tax_registration: next.has_tax_registration ?? prev?.has_tax_registration ?? null,
                sector: next.sector ?? prev?.sector ?? null,
                accounting_software: next.accounting_software ?? prev?.accounting_software ?? null,
                client_brief: next.client_brief ?? prev?.client_brief ?? null,
                monthly_invoices_count: next.monthly_invoices_count ?? prev?.monthly_invoices_count ?? null,
              }
            : null,
          isLoading: false,
        };
      });
    } catch (error: any) {
      set({ error: error.message || 'Error fetching organization', isLoading: false, organization: null });
    }
  },

  addOrganization: async (data: AddOrganization) => {
    set({ isLoading: true, error: null });
    try {
      const response = await addOrganizationService(data);
      let organization = extractOrganizationFromResponse(response);

      if (organization?.id) {
        organization = mergeOrganizationWithPayload(organization, data);
      } else if (hasOrganizationId(response)) {
        organization = mergeOrganizationWithPayload(response as Organization, data);
      } else {
        const listResponse = await getOrganizations({ limit: 500, offset: 0 });
        const match = ((listResponse.result_list ?? []) as Organization[]).find(
          (org) => org.email?.trim().toLowerCase() === data.email.trim().toLowerCase()
            || org.name?.trim().toLowerCase() === data.name.trim().toLowerCase(),
        );
        organization = match?.id ? mergeOrganizationWithPayload(match, data) : null;
      }

      if (!organization?.id) {
        throw new Error('Organization ID not returned from server');
      }

      set((state) => ({
        organizations: [...state.organizations.filter((org) => org.id !== organization.id), organization],
        organization,
        isLoading: false,
      }));
      return organization;
    } catch (error: any) {
      set({ error: error.message || 'Error adding organization', isLoading: false });
      throw error;
    }
  },

  updateOrganization: async (id: string, data: UpdateOrganizationRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateOrganization(id, data);
      const fromApi =
        extractOrganizationFromResponse(response)
        ?? (hasOrganizationId(response) ? (response as Organization) : null);
      set((state) => {
        const current = state.organization?.id === id ? state.organization : null;
        const base = fromApi ?? current ?? ({ id, name: data.name, email: data.email } as Organization);
        const merged = mergeOrganizationWithPayload(base, data);
        return {
          organizations: state.organizations.map((org) =>
            org.id === id ? mergeOrganizationWithPayload(org, data) : org,
          ),
          organization: current ? merged : state.organization,
          isLoading: false,
        };
      });
    } catch (error: any) {
      // Re-throw the error so the component can handle specific status codes
      set({ error: error.message || 'Error updating organization', isLoading: false });
      throw error;
    }
  },

  deleteOrganization: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await deleteOrganizationService(id);
      // Remove the deleted organization from the local list (soft delete on backend)
      set((state) => ({
        organizations: state.organizations.filter(org => org.id !== id),
        organization: state.organization?.id === id ? null : state.organization,
        isLoading: false
      }));
    } catch (error: any) {
      // Re-throw the error so the component can handle specific status codes (404, 409)
      set({ error: error.message || 'Error deleting organization', isLoading: false });
      throw error;
    }
  },
})); 